import React, { useState, useEffect } from 'react';
import './Feedback.css';

// Apna backend URL yahan set karo
const API_URL = "http://localhost:8000/api";

const Feedback = () => {
  const [activeTab, setActiveTab] = useState('recent');
  const [selectedFeature, setSelectedFeature] = useState('all');
  const [selectedRating, setSelectedRating] = useState('all');
  const [dateRange, setDateRange] = useState('last30');
  const [visibleFeedbacks, setVisibleFeedbacks] = useState(3);
  const [selectedFeedbacks, setSelectedFeedbacks] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({});
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Backend status ko frontend status mein convert karo
  const mapStatus = (status) => {
    if (status === 'pending') return 'recent';
    if (status === 'needs_action') return 'needsAction';
    if (status === 'resolved') return 'resolved';
    return 'recent';
  };

  // "kitne din pehle" wala text banao
  const getTimeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now - past;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'just now';
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  const getAvatar = (name) => {
    if (!name) return 'NA';
    const parts = name.split(' ');
    return parts.length > 1
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : name.substring(0, 2).toUpperCase();
  };

  // Backend se feedback fetch karo
  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/viewallfeedback`, {
        method: "POST"
      });
      const data = await res.json();

      if (data.success) {
        const formatted = data.feedback.map((f) => {
          const fullName = f.customer
            ? `${f.customer.firstname} ${f.customer.lastname}`
            : 'Customer';

          return {
          id: f.id,
          name: fullName,
          avatar: getAvatar(fullName),
          time: getTimeAgo(f.created_at),
          timestamp: new Date(f.created_at),
          feature: f.featureType,
          rating: f.rating,
          comment: f.comment,
          status: mapStatus(f.status),
          replied: !!f.admin_reply,
          replyText: f.admin_reply || ''
          };
        });
        setFeedbacks(formatted);
      }
    } catch (error) {
      console.log("Error fetching feedback:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const getTabCount = (tab) => {
    return feedbacks.filter(f => f.status === tab).length;
  };

  const filterByDateRange = (feedback) => {
    const now = new Date();
    const diffDays = Math.floor((now - feedback.timestamp) / (1000 * 60 * 60 * 24));

    switch (dateRange) {
      case 'last7': return diffDays <= 7;
      case 'last30': return diffDays <= 30;
      case 'last90': return diffDays <= 90;
      case 'all': return true;
      default: return true;
    }
  };

  const filteredFeedbacks = feedbacks.filter(feedback => {
    const matchesTab = feedback.status === activeTab;

    const matchesFeature = selectedFeature === 'all' ||
      (selectedFeature === 'tryon' && feedback.feature === 'AI VIRTUAL TRY-ON') ||
      (selectedFeature === 'custom' && feedback.feature === 'AI CUSTOMIZATION');

    const matchesRating = selectedRating === 'all' || feedback.rating === parseInt(selectedRating);

    const matchesDateRange = filterByDateRange(feedback);

    return matchesTab && matchesFeature && matchesRating && matchesDateRange;
  });

  const displayedFeedbacks = filteredFeedbacks.slice(0, visibleFeedbacks);

  const handleLoadMore = () => {
    setVisibleFeedbacks(prev => Math.min(prev + 5, filteredFeedbacks.length));
  };

  const handleSelectFeedback = (id) => {
    setSelectedFeedbacks(prev =>
      prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = () => {
    if (selectedFeedbacks.length === 0) return;

    setAlertConfig({
      title: 'Delete Feedback?',
      message: `Are you sure you want to delete ${selectedFeedbacks.length} feedback${selectedFeedbacks.length > 1 ? 's' : ''}? This action cannot be undone.`,
      type: 'warning',
      onConfirm: confirmDelete,
      onCancel: () => setShowAlert(false)
    });
    setShowAlert(true);
  };

  const confirmDelete = async () => {
    try {
      await fetch(`${API_URL}/deletefeedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: selectedFeedbacks })
      });

      setFeedbacks(prev => prev.filter(f => !selectedFeedbacks.includes(f.id)));
      setSelectedFeedbacks([]);
      setShowAlert(false);

      setAlertConfig({
        title: 'Deleted!',
        message: 'Feedback has been successfully deleted.',
        type: 'success',
        onConfirm: () => setShowAlert(false),
        showCancel: false
      });
      setShowAlert(true);
    } catch (error) {
      console.log("Error deleting feedback:", error);
    }
  };

  const handleMarkResolved = async (id) => {
    try {
      await fetch(`${API_URL}/markresolved`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: id })
      });

      setFeedbacks(prev => prev.map(f =>
        f.id === id ? { ...f, status: 'resolved' } : f
      ));

      setAlertConfig({
        title: 'Success!',
        message: 'Feedback has been marked as resolved.',
        type: 'success',
        onConfirm: () => setShowAlert(false),
        showCancel: false
      });
      setShowAlert(true);
    } catch (error) {
      console.log("Error marking resolved:", error);
    }
  };

  const handleReplyClick = (feedback) => {
    setReplyTo(feedback);
    setReplyMessage('');
    setShowReplyModal(true);
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) return;

    try {
      await fetch(`${API_URL}/replyfeedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: replyTo.id, admin_reply: replyMessage })
      });

      setFeedbacks(prev => prev.map(f =>
        f.id === replyTo.id ? { ...f, replied: true, replyText: replyMessage } : f
      ));

      setAlertConfig({
        title: 'Reply Sent!',
        message: `Your reply has been sent to ${replyTo.name}.`,
        type: 'success',
        onConfirm: () => setShowAlert(false),
        showCancel: false
      });
      setShowAlert(true);
      setShowReplyModal(false);
      setReplyTo(null);
      setReplyMessage('');
    } catch (error) {
      console.log("Error sending reply:", error);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <span key={i} className={`MOONLIT-FEEDBACK-star ${i < rating ? 'MOONLIT-FEEDBACK-filled' : ''}`}>★</span>
    ));
  };

  if (loading) {
    return <div className="MOONLIT-FEEDBACK-container">Loading...</div>;
  }

  return (
    <div className="MOONLIT-FEEDBACK-container">
      <div className="MOONLIT-FEEDBACK-header">
        <div className="MOONLIT-FEEDBACK-header-content">
          <h1 className="MOONLIT-FEEDBACK-title">AI Feature Feedback</h1>
          <p className="MOONLIT-FEEDBACK-subtitle">Manage and analyze customer sentiment for AI shopping tools.</p>
        </div>
        {selectedFeedbacks.length > 0 && (
          <button className="MOONLIT-FEEDBACK-delete-btn" onClick={handleDeleteSelected}>
            <span className="MOONLIT-FEEDBACK-delete-icon">🗑️</span>
            Delete Selected ({selectedFeedbacks.length})
          </button>
        )}
      </div>

      <div className="MOONLIT-FEEDBACK-filters">
        <div className="MOONLIT-FEEDBACK-filter-group">
          <label className="MOONLIT-FEEDBACK-filter-label">FEATURE TYPE</label>
          <div className="MOONLIT-FEEDBACK-filter-buttons">
            <button
              className={`MOONLIT-FEEDBACK-filter-btn ${selectedFeature === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedFeature('all')}
            >
              All
            </button>
            <button
              className={`MOONLIT-FEEDBACK-filter-btn ${selectedFeature === 'tryon' ? 'active' : ''}`}
              onClick={() => setSelectedFeature('tryon')}
            >
              Try-on
            </button>
            <button
              className={`MOONLIT-FEEDBACK-filter-btn ${selectedFeature === 'custom' ? 'active' : ''}`}
              onClick={() => setSelectedFeature('custom')}
            >
              Custom
            </button>
          </div>
        </div>

        <div className="MOONLIT-FEEDBACK-filter-group">
          <label className="MOONLIT-FEEDBACK-filter-label">RATING</label>
          <select
            className="MOONLIT-FEEDBACK-select"
            value={selectedRating}
            onChange={(e) => setSelectedRating(e.target.value)}
          >
            <option value="all">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>
        </div>

        <div className="MOONLIT-FEEDBACK-filter-group">
          <label className="MOONLIT-FEEDBACK-filter-label">DATE RANGE</label>
          <select
            className="MOONLIT-FEEDBACK-select"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="last7">Last 7 Days</option>
            <option value="last30">Last 30 Days</option>
            <option value="last90">Last 90 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      <div className="MOONLIT-FEEDBACK-tabs">
        <button
          className={`MOONLIT-FEEDBACK-tab-btn ${activeTab === 'recent' ? 'active' : ''}`}
          onClick={() => { setActiveTab('recent'); setVisibleFeedbacks(3); }}
        >
          Recent Feedback ({getTabCount('recent')})
        </button>
        <button
          className={`MOONLIT-FEEDBACK-tab-btn ${activeTab === 'needsAction' ? 'active' : ''}`}
          onClick={() => { setActiveTab('needsAction'); setVisibleFeedbacks(3); }}
        >
          Needs Action ({getTabCount('needsAction')})
        </button>
        <button
          className={`MOONLIT-FEEDBACK-tab-btn ${activeTab === 'resolved' ? 'active' : ''}`}
          onClick={() => { setActiveTab('resolved'); setVisibleFeedbacks(3); }}
        >
          Resolved ({getTabCount('resolved')})
        </button>
      </div>

      <div className="MOONLIT-FEEDBACK-list">
        {displayedFeedbacks.length === 0 && (
          <p style={{ textAlign: 'center', padding: '20px' }}>No feedback found.</p>
        )}

        {displayedFeedbacks.map((feedback, index) => (
          <div
            key={feedback.id}
            className={`MOONLIT-FEEDBACK-card ${selectedFeedbacks.includes(feedback.id) ? 'MOONLIT-FEEDBACK-selected' : ''} ${feedback.status === 'resolved' ? 'MOONLIT-FEEDBACK-resolved-card' : ''}`}
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="MOONLIT-FEEDBACK-header-section">
              <div className="MOONLIT-FEEDBACK-user">
                <input
                  type="checkbox"
                  className="MOONLIT-FEEDBACK-checkbox"
                  checked={selectedFeedbacks.includes(feedback.id)}
                  onChange={() => handleSelectFeedback(feedback.id)}
                />
                <div className="MOONLIT-FEEDBACK-avatar">{feedback.avatar}</div>
                <div className="MOONLIT-FEEDBACK-user-info">
                  <h3 className="MOONLIT-FEEDBACK-user-name">{feedback.name}</h3>
                  <div className="MOONLIT-FEEDBACK-meta">
                    <span className="MOONLIT-FEEDBACK-time">{feedback.time}</span>
                    <span className="MOONLIT-FEEDBACK-badge">{feedback.feature}</span>
                  </div>
                </div>
              </div>
              <div className="MOONLIT-FEEDBACK-rating">
                {renderStars(feedback.rating)}
              </div>
            </div>

            <p className="MOONLIT-FEEDBACK-comment">{feedback.comment}</p>

            {feedback.replied && feedback.replyText && (
              <div className="MOONLIT-FEEDBACK-reply-box">
                <div className="MOONLIT-FEEDBACK-reply-header">
                  <span className="MOONLIT-FEEDBACK-reply-icon">↩️</span>
                  <span className="MOONLIT-FEEDBACK-reply-label">Your Reply:</span>
                </div>
                <p className="MOONLIT-FEEDBACK-reply-text">{feedback.replyText}</p>
              </div>
            )}

            <div className="MOONLIT-FEEDBACK-actions">
              <button
                className="MOONLIT-FEEDBACK-action-btn MOONLIT-FEEDBACK-reply-btn"
                onClick={() => handleReplyClick(feedback)}
              >
                <span>↩</span> Reply to {feedback.name.split(' ')[0]}
              </button>
              <button
                className={`MOONLIT-FEEDBACK-action-btn MOONLIT-FEEDBACK-resolve-btn ${feedback.status === 'resolved' ? 'MOONLIT-FEEDBACK-resolved' : ''}`}
                onClick={() => handleMarkResolved(feedback.id)}
                disabled={feedback.status === 'resolved'}
              >
                <span className="MOONLIT-FEEDBACK-resolve-icon">{feedback.status === 'resolved' ? '✓' : '○'}</span>
                {feedback.status === 'resolved' ? 'Resolved' : 'Mark Resolved'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {visibleFeedbacks < filteredFeedbacks.length && (
        <div className="MOONLIT-FEEDBACK-load-more">
          <button className="MOONLIT-FEEDBACK-load-more-btn" onClick={handleLoadMore}>
            Load More Feedback
          </button>
        </div>
      )}

      {showReplyModal && replyTo && (
        <div className="MOONLIT-FEEDBACK-modal-overlay" onClick={() => setShowReplyModal(false)}>
          <div className="MOONLIT-FEEDBACK-reply-modal" onClick={(e) => e.stopPropagation()}>
            <div className="MOONLIT-FEEDBACK-modal-header">
              <h2>Reply to {replyTo.name}</h2>
              <button className="MOONLIT-FEEDBACK-close-modal-btn" onClick={() => setShowReplyModal(false)}>×</button>
            </div>
            <div className="MOONLIT-FEEDBACK-modal-body">
              <div className="MOONLIT-FEEDBACK-original-feedback">
                <p className="MOONLIT-FEEDBACK-original-label">Original Feedback:</p>
                <p className="MOONLIT-FEEDBACK-original-text">{replyTo.comment}</p>
              </div>
              <textarea
                className="MOONLIT-FEEDBACK-reply-textarea"
                placeholder="Write your reply here..."
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                rows={6}
              />
            </div>
            <div className="MOONLIT-FEEDBACK-modal-footer">
              <button className="MOONLIT-FEEDBACK-modal-btn MOONLIT-FEEDBACK-cancel-modal-btn" onClick={() => setShowReplyModal(false)}>
                Cancel
              </button>
              <button
                className="MOONLIT-FEEDBACK-modal-btn MOONLIT-FEEDBACK-send-reply-btn"
                onClick={handleSendReply}
                disabled={!replyMessage.trim()}
              >
                Send Reply
              </button>
            </div>
          </div>
        </div>
      )}

      {showAlert && (
        <div className="MOONLIT-FEEDBACK-alert-overlay">
          <div className={`MOONLIT-FEEDBACK-alert ${alertConfig.type === 'warning' ? 'MOONLIT-FEEDBACK-warning' : ''}`}>
            <div className="MOONLIT-FEEDBACK-alert-icon">
              {alertConfig.type === 'warning' ? '⚠️' : '✓'}
            </div>
            <h2 className="MOONLIT-FEEDBACK-alert-title">{alertConfig.title}</h2>
            <p className="MOONLIT-FEEDBACK-alert-message">{alertConfig.message}</p>
            <div className="MOONLIT-FEEDBACK-alert-actions">
              {alertConfig.showCancel !== false && (
                <button className="MOONLIT-FEEDBACK-alert-btn MOONLIT-FEEDBACK-cancel-btn" onClick={alertConfig.onCancel}>
                  Cancel
                </button>
              )}
              <button
                className={`MOONLIT-FEEDBACK-alert-btn MOONLIT-FEEDBACK-confirm-btn ${alertConfig.type === 'warning' ? 'MOONLIT-FEEDBACK-danger' : 'MOONLIT-FEEDBACK-success'}`}
                onClick={alertConfig.onConfirm}
              >
                {alertConfig.type === 'warning' ? 'Yes, Delete' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feedback;